
import { useState, useEffect, useMemo } from 'react';
import { Article } from '@/data/articlesData';

export const useArticleFiltering2 = (articles: Article[]) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleArticles, setVisibleArticles] = useState(12); // Increased initial display count

  // Reset visible articles when category or search changes
  useEffect(() => {
    setVisibleArticles(12); // Increased initial display count
  }, [activeCategory, searchTerm]);

  // Debug log filtered articles
  useEffect(() => {
    console.log(`Filtering articles with category: ${activeCategory}, search: ${searchTerm}`);
    console.log(`Available articles: ${articles.length}`);
    
    // Debug: log all article slugs to help troubleshoot
    if (process.env.NODE_ENV === 'development') {
      console.log("All available article slugs:", articles.map(a => a.slug).sort());
    }
  }, [articles, activeCategory, searchTerm]);

  // Filter articles by category and search term
  const filteredArticles = useMemo(() => {
    // First filter by category and search term
    const filtered = articles.filter(article => {
      // Filter by category
      const categoryMatch = activeCategory === 'all' || article.category === activeCategory;
      
      // Filter by search term
      const searchLower = searchTerm.toLowerCase();
      const searchMatch = !searchTerm || 
        article.title.toLowerCase().includes(searchLower) ||
        article.summary?.toLowerCase().includes(searchLower) ||
        article.description?.toLowerCase().includes(searchLower) ||
        article.category.toLowerCase().includes(searchLower) ||
        article.slug.toLowerCase().includes(searchLower);
      
      return categoryMatch && searchMatch;
    });
    
    // Then remove duplicates based on slug (keeping the newest version)
    const uniqueSlugs = new Set<string>();
    const uniqueArticles = filtered
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by date (newest first)
      .filter(article => {
        // If we haven't seen this slug before, keep it
        if (!uniqueSlugs.has(article.slug)) {
          uniqueSlugs.add(article.slug);
          return true;
        }
        return false;
      });
    
    console.log(`Filtered articles: ${uniqueArticles.length}`);
    return uniqueArticles;
  }, [articles, activeCategory, searchTerm]);

  // Load more articles - increased batch size
  const loadMore = () => {
    setVisibleArticles(prev => prev + 9);
  };

  return {
    filteredArticles,
    visibleArticles,
    activeCategory,
    searchTerm,
    setActiveCategory,
    setSearchTerm,
    loadMore
  };
};
